import { $enum } from 'ts-enum-util'

export interface IRegion {
  abbr: string
  name: string
  statesArray: string[]
  regionsArray: string[]
}

export interface IState {
  abbr: string
  name: string
}

export interface IStates {
  US: IState[]
  CA: IState[]
  undefined: IState[]
}

export interface ICountry {
  name: string
  code: string
}

export enum REGION_GROUP_CARRIER_SALES {
  'C' = 'Coastal',
  'MW' = 'Midwest',
  'N' = 'Northeast',
  'S' = 'South',
  'SE' = 'Southeast',
  'W' = 'West',
}

export interface IMultiSelectOptions {
  label: REGION_GROUP_CARRIER_SALES
  value: keyof typeof REGION_GROUP_CARRIER_SALES
  selected: boolean
}

export const convertToRegion = (r?: string | null) => {
  return $enum(REGION_GROUP_CARRIER_SALES).getValueOrDefault(r, 'Unknown')
}

export const carrierSalesRegions = (): IMultiSelectOptions[] => {
  return $enum(REGION_GROUP_CARRIER_SALES).map((value, key) => {
    return {
      label: value,
      value: key,
      selected: false,
    }
  })
}

const coastal = ['DC', 'DE', 'MD', 'NC', 'SC', 'VA']
const west = ['CA', 'OR', 'WA', 'ID', 'UT', 'AZ', 'NM', 'CO', 'WY', 'MT', 'NV']
const northeast = ['PA', 'NJ', 'NY', 'VT', 'NH', 'MA', 'RI', 'CT', 'ME']
const south = ['TX', 'LA', 'AR', 'OK']
const southeast = ['FL', 'GA', 'AL', 'MS', 'TN']
const bookingSouth = ['TX', 'LA', 'AR', 'OK', 'AZ', 'NM']
const bookingWest = ['CA', 'OR', 'WA', 'ID', 'UT', 'CO', 'WY', 'MT', 'NV']
const midwestEast = ['WV', 'KY', 'OH', 'MI', 'IN']
const midwestCentral = ['IL', 'MO', 'KS', 'NE', 'SD', 'ND', 'MN', 'IA', 'WI']
const midwest = [...midwestEast, ...midwestCentral]

export const REGIONS: IRegion[] = [
  {
    abbr: 'C',
    name: 'Coastal',
    statesArray: coastal,
    regionsArray: ['C'],
  },
  {
    abbr: 'S',
    name: 'South',
    statesArray: south,
    regionsArray: ['S'],
  },
  {
    abbr: 'W',
    name: 'West',
    statesArray: west,
    regionsArray: ['W'],
  },
  {
    abbr: 'NE',
    name: 'Northeast',
    statesArray: northeast,
    regionsArray: ['N'],
  },
  {
    abbr: 'MW',
    name: 'Midwest',
    statesArray: midwest,
    regionsArray: ['MW'],
  },
  {
    abbr: 'SE',
    name: 'Southeast',
    statesArray: southeast,
    regionsArray: ['SE'],
  },
  {
    abbr: 'SEC',
    name: 'Southeast + Coastal',
    statesArray: [...southeast, ...coastal],
    regionsArray: ['SE', 'C'],
  },
  {
    abbr: 'W + S',
    name: 'West + South',
    statesArray: [...west, ...south],
    regionsArray: ['W', 'S'],
  },
  {
    abbr: 'NE + SEC',
    name: 'Northeast + Southeast + Coastal',
    statesArray: [...northeast, ...southeast, ...coastal],
    regionsArray: ['N', 'SE', 'C'],
  },
  {
    abbr: 'MW + W + S',
    name: 'Midwest + West + South',
    statesArray: [...midwest, ...west, ...south],
    regionsArray: ['MW', 'W', 'S'],
  },
]

export const BOOKING_REGIONS: IRegion[] = [
  {
    abbr: 'C',
    name: 'Coastal',
    statesArray: coastal,
    regionsArray: ['C'],
  },
  {
    abbr: 'S',
    name: 'South',
    statesArray: bookingSouth,
    regionsArray: ['S'],
  },
  {
    abbr: 'W',
    name: 'West',
    statesArray: bookingWest,
    regionsArray: ['W'],
  },
  {
    abbr: 'NE',
    name: 'Northeast',
    statesArray: northeast,
    regionsArray: ['N'],
  },
  {
    abbr: 'SE',
    name: 'Southeast',
    statesArray: southeast,
    regionsArray: ['SE'],
  },
  {
    abbr: 'SEC',
    name: 'Southeast + Coastal',
    statesArray: [...southeast, ...coastal],
    regionsArray: ['SE', 'C'],
  },
  {
    abbr: 'MW(E)',
    name: 'Midwest (East)',
    statesArray: midwestEast,
    regionsArray: ['MWE'],
  },
  {
    abbr: 'MW(Central)',
    name: 'Midwest (Central)',
    statesArray: midwestCentral,
    regionsArray: ['MWC'],
  },
  {
    abbr: 'MW (E + Central)',
    name: 'Midwest',
    statesArray: [...midwestEast, ...midwestCentral],
    regionsArray: ['MWC', 'MWE'],
  },
  {
    abbr: 'NE + C',
    name: 'Northeast + Coastal',
    statesArray: [...northeast, ...coastal],
    regionsArray: ['N', 'C'],
  },
  {
    abbr: 'W + S',
    name: 'West + South',
    statesArray: [...west, ...south],
    regionsArray: ['W', 'S'],
  },
  {
    abbr: 'NE + SEC',
    name: 'Northeast + Southeast + Coastal',
    statesArray: [...northeast, ...southeast, ...coastal],
    regionsArray: ['N', 'SE', 'C'],
  },
  {
    abbr: 'MWE + MWC + W + S',
    name: 'Midwest + West + South',
    statesArray: [...midwestEast, ...midwestCentral, ...west, ...south],
    regionsArray: ['MWE', 'MWC', 'W', 'S'],
  },
]

export const IBOOKING_REGIONS_OPTIONS = [
  {
    label: 'Coastal',
    value: 'C',
    selected: false,
  },
  {
    label: 'Midwest East',
    value: 'MWE',
    selected: false,
  },
  {
    label: 'Midwest Central',
    value: 'MWC',
    selected: false,
  },
  {
    label: 'Northeast',
    value: 'N',
    selected: false,
  },
  {
    label: 'South',
    value: 'S',
    selected: false,
  },
  {
    label: 'Southeast',
    value: 'SE',
    selected: false,
  },
  {
    label: 'West',
    value: 'W',
    selected: false,
  },
]

export const IREGIONS_OPTIONS = [
  {
    label: 'Canada',
    value: 'CA',
    selected: false,
  },
  {
    label: 'Coastal',
    value: 'C',
    selected: false,
  },
  {
    label: 'Midwest',
    value: 'MW',
    selected: false,
  },
  {
    label: 'Northeast',
    value: 'N',
    selected: false,
  },
  {
    label: 'South',
    value: 'S',
    selected: false,
  },
  {
    label: 'Southeast',
    value: 'SE',
    selected: false,
  },
  {
    label: 'West',
    value: 'W',
    selected: false,
  },
]

export enum REGION_ABBREVIATIONS {
  C = 'C',
  S = 'S',
  W = 'W',
  N = 'N',
  MW = 'MW',
  SE = 'SE',
}

export const STATES: IStates = {
  US: [
    {
      name: 'Alabama',
      abbr: 'AL',
    },
    {
      name: 'Alaska',
      abbr: 'AK',
    },
    {
      name: 'Arizona',
      abbr: 'AZ',
    },
    {
      name: 'Arkansas',
      abbr: 'AR',
    },
    {
      name: 'California',
      abbr: 'CA',
    },
    {
      name: 'Colorado',
      abbr: 'CO',
    },
    {
      name: 'Connecticut',
      abbr: 'CT',
    },
    {
      name: 'Delaware',
      abbr: 'DE',
    },
    {
      name: 'District Of Columbia',
      abbr: 'DC',
    },
    {
      name: 'Florida',
      abbr: 'FL',
    },
    {
      name: 'Georgia',
      abbr: 'GA',
    },
    {
      name: 'Hawaii',
      abbr: 'HI',
    },
    {
      name: 'Idaho',
      abbr: 'ID',
    },
    {
      name: 'Illinois',
      abbr: 'IL',
    },
    {
      name: 'Indiana',
      abbr: 'IN',
    },
    {
      name: 'Iowa',
      abbr: 'IA',
    },
    {
      name: 'Kansas',
      abbr: 'KS',
    },
    {
      name: 'Kentucky',
      abbr: 'KY',
    },
    {
      name: 'Louisiana',
      abbr: 'LA',
    },
    {
      name: 'Maine',
      abbr: 'ME',
    },
    {
      name: 'Maryland',
      abbr: 'MD',
    },
    {
      name: 'Massachusetts',
      abbr: 'MA',
    },
    {
      name: 'Michigan',
      abbr: 'MI',
    },
    {
      name: 'Minnesota',
      abbr: 'MN',
    },
    {
      name: 'Mississippi',
      abbr: 'MS',
    },
    {
      name: 'Missouri',
      abbr: 'MO',
    },
    {
      name: 'Montana',
      abbr: 'MT',
    },
    {
      name: 'Nebraska',
      abbr: 'NE',
    },
    {
      name: 'Nevada',
      abbr: 'NV',
    },
    {
      name: 'New Hampshire',
      abbr: 'NH',
    },
    {
      name: 'New Jersey',
      abbr: 'NJ',
    },
    {
      name: 'New Mexico',
      abbr: 'NM',
    },
    {
      name: 'New York',
      abbr: 'NY',
    },
    {
      name: 'North Carolina',
      abbr: 'NC',
    },
    {
      name: 'North Dakota',
      abbr: 'ND',
    },
    {
      name: 'Ohio',
      abbr: 'OH',
    },
    {
      name: 'Oklahoma',
      abbr: 'OK',
    },
    {
      name: 'Oregon',
      abbr: 'OR',
    },
    {
      name: 'Pennsylvania',
      abbr: 'PA',
    },
    {
      name: 'Rhode Island',
      abbr: 'RI',
    },
    {
      name: 'South Carolina',
      abbr: 'SC',
    },
    {
      name: 'South Dakota',
      abbr: 'SD',
    },
    {
      name: 'Tennessee',
      abbr: 'TN',
    },
    {
      name: 'Texas',
      abbr: 'TX',
    },
    {
      name: 'Utah',
      abbr: 'UT',
    },
    {
      name: 'Vermont',
      abbr: 'VT',
    },
    {
      name: 'Virginia',
      abbr: 'VA',
    },
    {
      name: 'Washington',
      abbr: 'WA',
    },
    {
      name: 'West Virginia',
      abbr: 'WV',
    },
    {
      name: 'Wisconsin',
      abbr: 'WI',
    },
    {
      name: 'Wyoming',
      abbr: 'WY',
    },
  ],
  CA: [
    {
      name: 'Alberta',
      abbr: 'AB',
    },
    {
      name: 'British Columbia',
      abbr: 'BC',
    },
    {
      name: 'Manitoba',
      abbr: 'MB',
    },
    {
      name: 'New Brunswick',
      abbr: 'NB',
    },
    {
      name: 'Newfoundland & Labrador',
      abbr: 'NL',
    },
    {
      name: 'Northwest Territories',
      abbr: 'NT',
    },
    {
      name: 'Nova Scotia',
      abbr: 'NS',
    },
    {
      name: 'Nunavut',
      abbr: 'NU',
    },
    {
      name: 'Ontario',
      abbr: 'ON',
    },
    {
      name: 'Prince Edward Island',
      abbr: 'PE',
    },
    {
      name: 'Québec',
      abbr: 'QC',
    },
    {
      name: 'Saskatchewan',
      abbr: 'SK',
    },
    {
      name: 'Yukon',
      abbr: 'YK',
    },
  ],
  get undefined() {
    return this.US
  },
}

export type TCountryCode = keyof typeof STATES

export const COUNTRIES: ICountry[] = [
  {
    name: 'United States',
    code: 'US',
  },
  {
    name: 'Canada',
    code: 'CA',
  },
]

export interface IStateOption {
  label: string
  value: string
}

export const usStatesAbbrOptions = STATES.US.map(value => ({
  value: value.abbr,
  label: value.abbr,
}))

export const usStatesOptions = STATES.US.map(state => ({ label: state.name, value: state.abbr }))
export const canadianStatesOptions = STATES.CA.map(state => ({
  label: state.name,
  value: state.abbr,
}))
export const countriesOptions = COUNTRIES.map(country => ({
  label: country.name,
  value: country.code,
}))
